import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { authApi } from "../../lib/authApi";
import { PartCrop } from "./UniversalShared";

const useBlob = (url, enabled) => {
  const [src, setSrc] = useState(null), [state, setState] = useState("loading");
  useEffect(() => {
    if (!enabled || !url) return undefined;
    let active = true, obj;
    setState("loading");
    authApi.get(url, { responseType: "blob" }).then(r => { if (!active) return; obj = URL.createObjectURL(r.data); setSrc(obj); setState("ok"); }).catch(() => active && setState("error"));
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url, enabled]);
  return [src, state];
};

export const PartZoomDialog = ({ rfqId, part, index, files, open, onOpenChange }) => {
  const ref = part?.geometry?.region;
  const file = files?.find(f => f.id === ref?.file_id);
  const isDxf = !!part?.geometry?.dxf;
  const [pageSrc, pageState] = useBlob(ref ? `/rfqs/${rfqId}/understanding/files/${ref.file_id}/pages/${ref.page}` : null, open && !!ref);
  const r = ref?.region;
  const whole = r && r[0] === 0 && r[1] === 0 && r[2] === 1 && r[3] === 1;
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="uu-zoom-dialog">
      <DialogHeader>
        <DialogTitle data-testid="uu-zoom-title">Part {index + 1} — {part?.fields?.name?.value || "Unnamed"}</DialogTitle>
        <DialogDescription>{isDxf ? "Left: isolated geometry rendered from your DXF. Right: the same region highlighted on the full drawing (region calculated from geometry extents)." : "Left: crop from your uploaded page. Right: the AI-estimated region highlighted on the original page — verify against the drawing."}</DialogDescription>
      </DialogHeader>
      {part && <div className="grid gap-4 md:grid-cols-2">
        <div><p className="font-mono-tech text-xs text-muted-foreground uppercase tracking-wider mb-2">Part crop</p><PartCrop rfqId={rfqId} part={part} className="max-h-[60vh] object-contain" testId="uu-zoom-crop" /></div>
        <div><p className="font-mono-tech text-xs text-muted-foreground uppercase tracking-wider mb-2">Evidence on original {ref ? `· ${file?.original_filename || file?.name || "uploaded file"} · page ${ref.page}` : ""}</p>
          {!ref && <p className="text-sm text-muted-foreground" data-testid="uu-zoom-no-region">No region recorded for this part.</p>}
          {ref && pageState === "loading" && <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {ref && pageState === "error" && <p className="text-sm text-destructive" data-testid="uu-zoom-page-error">Original page preview unavailable.</p>}
          {ref && pageState === "ok" && <div className="relative inline-block max-w-full border border-border rounded-md bg-white" data-testid="uu-zoom-page">
            <img src={pageSrc} alt="Original drawing page" className="block max-w-full max-h-[60vh] h-auto" />
            {!whole && <div data-testid="uu-zoom-region" className="absolute border-2 border-primary bg-primary/10 pointer-events-none" style={{ left: `${r[0] * 100}%`, top: `${r[1] * 100}%`, width: `${(r[2] - r[0]) * 100}%`, height: `${(r[3] - r[1]) * 100}%` }} />}
            {whole && <p className="absolute bottom-1 left-1 text-[10px] bg-white/80 px-1 rounded" data-testid="uu-zoom-whole">Whole drawing</p>}
          </div>}
        </div>
      </div>}
    </DialogContent>
  </Dialog>;
};
